import { BillingProcess, LimitNames, PlanTypes } from '@gonfalon/billing/types';

import { type Plan, CheckoutProcess } from '../types';

import {
  aiRunsProductDisplayOnly,
  aiRunsProduct,
  errorsProduct,
  experimentationProduct,
  logsProduct,
  sessionsProduct,
  tracesProduct,
} from './products';
import {
  developerPlus,
  scalableExperimentation,
  scalableObservability,
  sso,
  thirtyDayRetention,
  unlimitedProjects,
  usageBasedAIRunsPricing,
  userTargeting,
} from './sharedFeatures';

export const foundationPlan: Plan = {
  id: PlanTypes.FOUNDATION2023,
  planType: PlanTypes.FOUNDATION2023,
  billingProcess: BillingProcess.USAGE_BASED_BILLING,
  checkoutProcess: CheckoutProcess.CUSTOMIZE_COMMITS,
  isFoundationPlan: true, // Foundation2023 requires payment card
  useObservabilityFlagForVersion: true, // Foundation2023 uses version 2 when observability flag is enabled
  recommended: false,
  marketingCopy: {
    name: 'Foundation',
    tagLine: 'A complete platform for startups and growing teams',
    annualDiscount: '2 free months',
    pricingHeadline: 'Pay-as-you-go',
    pricingDescription: 'Customize your plan. Billed monthly, or save annually.',
    featuresHeader: 'Everything in Developer, and',
    features: [
      { text: 'Unlimited projects and environments' },
      { text: 'A/B tests and experimentation' },
      {
        text: 'First-class mobile SDKs, attributes and targeting',
      },
      { text: 'Comprehensive audit log' },
    ],
  },
  versions: {
    1: {
      planTitle: 'Foundation Plan',
      products: [
        {
          limitName: LimitNames.HOSTS,
          freeQuantity: 5,
          annualCommitDetails: {
            blockSize: 1,
            pricePerBlockCents: 12_000,
            maxQuantity: 500,
          },
          monthlyOverageDetails: {
            blockSize: 1,
            pricePerBlockCents: 1200,
          },
        },
        {
          limitName: LimitNames.MONTHLY_ACTIVE_USERS,
          freeQuantity: 1000,
          annualCommitDetails: {
            blockSize: 1000,
            pricePerBlockCents: 10_000,
            maxQuantity: 300_000,
          },
          monthlyOverageDetails: {
            blockSize: 1000,
            pricePerBlockCents: 1000,
          },
        },
        aiRunsProductDisplayOnly,
        experimentationProduct,
      ],
    },
    2: {
      planTitle: 'Foundation Plan',
      products: [
        {
          limitName: LimitNames.HOSTS,
          freeQuantity: 5,
          annualCommitDetails: {
            blockSize: 1,
            pricePerBlockCents: 12_000,
            maxQuantity: 500,
          },
          monthlyOverageDetails: {
            blockSize: 1,
            pricePerBlockCents: 1200,
          },
        },
        {
          limitName: LimitNames.MONTHLY_ACTIVE_USERS,
          freeQuantity: 1000,
          annualCommitDetails: {
            blockSize: 1000,
            pricePerBlockCents: 10_000,
            maxQuantity: 300_000,
          },
          monthlyOverageDetails: {
            blockSize: 1000,
            pricePerBlockCents: 1000,
          },
        },
        aiRunsProductDisplayOnly,
        sessionsProduct,
        errorsProduct,
        tracesProduct,
        logsProduct,
        experimentationProduct,
      ],
    },
    3: {
      planTitle: 'Foundation Plan',
      marketingCopy: {
        tagLine: 'Ship features and agents to production. Scale both without hitting a ceiling.',
        pricingDescription: 'Billed monthly, or save annually',
        featuresHeader: '',
        features: [],
        featureGroups: [
          {
            header: 'CodeControl',
            features: [
              developerPlus,
              unlimitedProjects,
              userTargeting,
              scalableObservability,
              scalableExperimentation,
              sso,
            ],
          },
          {
            header: 'AgentControl',
            features: [
              usageBasedAIRunsPricing,
              userTargeting,
              scalableObservability,
              scalableExperimentation,
              sso,
              thirtyDayRetention,
            ],
          },
        ],
      },
      products: [
        {
          limitName: LimitNames.HOSTS,
          freeQuantity: 5,
          annualCommitDetails: {
            blockSize: 1,
            pricePerBlockCents: 12_000,
            maxQuantity: 500,
          },
          monthlyOverageDetails: {
            blockSize: 1,
            pricePerBlockCents: 1200,
          },
        },
        {
          limitName: LimitNames.MONTHLY_ACTIVE_USERS,
          freeQuantity: 1000,
          annualCommitDetails: {
            blockSize: 1000,
            pricePerBlockCents: 10_000,
            maxQuantity: 300_000,
          },
          monthlyOverageDetails: {
            blockSize: 1000,
            pricePerBlockCents: 1000,
          },
        },
        aiRunsProduct,
        sessionsProduct,
        errorsProduct,
        tracesProduct,
        logsProduct,
        experimentationProduct,
      ],
    },
    // AC-only tier. Selectable by trial, Developer, and pre-Foundation2023 plans.
    // Foundation2023 subscribers are blocked (disableIf) to prevent downgrading once CodeControl is included.
    4: {
      planTitle: 'Foundation Plan',
      hasAnnualBilling: false,
      disableIf: [{ planType: PlanTypes.FOUNDATION2023 }],
      marketingCopy: {
        tagLine: 'Ship agents to production. Scale usage without hitting a ceiling.',
        pricingDescription: '$5 per 1k AI runs past 5k / mo',
        featuresHeader: 'Everything in Developer, plus',
        features: [
          usageBasedAIRunsPricing,
          userTargeting,
          scalableObservability,
          scalableExperimentation,
          sso,
          thirtyDayRetention,
        ],
      },
      products: [aiRunsProduct, sessionsProduct, errorsProduct, tracesProduct, logsProduct, experimentationProduct],
    },
  },
};
